import { useRef, useState } from "react";
import type { Stage as KonvaStage } from "konva/lib/Stage";
import { useLocation, useNavigate } from "react-router";
import { Bookmark, Heart, MessageCircle, Send } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";
import { getSocialPostPreviewCardFormatDescriptor } from "@/components/campaign/social-post-preview-card";
import { SocialPostPreview } from "@/components/campaign/social-post-preview";
import { buildFocusedPostEditorPath } from "@/lib/focused-post-editor";
import { buildBrandPath } from "@/hooks/use-route-brand";
import { downloadSocialPostPreviewImage } from "@/lib/social-post-preview-download";
import {
  getCalendarApprovalLabel,
  getCalendarPostReviewActions,
  type CalendarPostLike,
} from "@/lib/calendar-posts";
import {
  resolveScheduledPostPreview,
  type ScheduledPostDraft,
  type ScheduledPostPreviewLogoAsset,
} from "@/lib/scheduled-post-preview";
import { cn } from "@/lib/utils";
import { Button } from "@runcheetah/ui";
import { PostPreviewOverflowMenu } from "./post-preview-overflow-menu";

type ApprovalPost = CalendarPostLike & {
  output?: unknown;
  draft?: unknown;
};

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  hour: "numeric",
  minute: "2-digit",
});

export function getApprovalCardCopy(
  caption: string,
  fallbackTitle = "Caption pending",
  fallbackExcerpt = "Preview copy will appear here once generation completes.",
) {
  const normalized = caption.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return {
      title: fallbackTitle,
      excerpt: fallbackExcerpt,
    };
  }

  const sentenceMatch = normalized.match(/(.+?[.!?])(?:\s|$)/);
  const title = (sentenceMatch?.[1] ?? normalized).trim();
  const excerpt = normalized.slice(title.length).trim();

  return { title, excerpt };
}

export function ApprovalPostCard({
  post,
  brandId,
  profileLabel,
  className,
  logoAssets,
  onOpen,
  approving,
  rejecting,
  publishing,
  onApprove,
  onReject,
  onPublishNow,
}: {
  post: ApprovalPost;
  brandId: string;
  profileLabel: string;
  className?: string;
  logoAssets?: ScheduledPostPreviewLogoAsset[];
  onOpen?: () => void;
  approving: boolean;
  rejecting: boolean;
  publishing: boolean;
  onApprove: () => void;
  onReject: () => void;
  onPublishNow: () => void;
}) {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const sceneStageRef = useRef<KonvaStage | null>(null);
  const preview = resolveScheduledPostPreview({
    output: post.output,
    draft: (post.draft ?? null) as ScheduledPostDraft | null,
    logoAssets,
  });
  const actions = getCalendarPostReviewActions(post);
  const canEdit = post.approvalStatus !== "approved";
  const statusLabel = getCalendarApprovalLabel(post, intl);
  const showStatusBadge = post.approvalStatus !== "pending" || post.status !== "generated";
  const focusedEditorPath = buildFocusedPostEditorPath({
    brandId,
    owner: {
      type: post.owner.type,
      id: post.owner.id,
    },
    postId: post.id,
  });
  const scheduledLabel = timeFormatter.format(new Date(post.scheduledFor)).replace(",", " ·");
  const postTypeLabel = intl.formatMessage(
    getSocialPostPreviewCardFormatDescriptor(preview.plan?.postType),
  );
  const { title, excerpt } = getApprovalCardCopy(
    preview.caption,
    intl.formatMessage({ id: "approvals.captionPending", defaultMessage: "Caption pending" }),
    intl.formatMessage({
      id: "approvals.previewCopyPending",
      defaultMessage: "Preview copy will appear here once generation completes.",
    }),
  );
  const captionFallback = intl.formatMessage({
    id: "approvals.openPostToEditCaption",
    defaultMessage: "Open the post to edit the caption before approving.",
  });
  const profileInitials = profileLabel.slice(0, 2).toUpperCase() || "BR";
  const openFocusedEditor = () => {
    navigate(focusedEditorPath, { state: { backgroundLocation: location } });
  };
  const openInstagramIntegrations = () => {
    navigate(buildBrandPath(brandId, "/integrations"));
  };

  const downloadImage = async () => {
    setDownloading(true);

    try {
      await downloadSocialPostPreviewImage({
        stage: sceneStageRef.current,
        previewAssetUrl: preview.previewAsset?.signedUrl ?? null,
        fileName: `${post.owner.type}-${post.id}.png`,
      });
    } catch (caught) {
      toast.error(
        caught instanceof Error
          ? caught.message
          : intl.formatMessage({
              id: "approvals.downloadFailed",
              defaultMessage: "Could not download the image.",
            }),
      );
    } finally {
      setDownloading(false);
    }
  };

  const visualContent = (
    <div className="relative w-full">
      {preview.plan ? (
        <SocialPostPreview
          renderMode="preview"
          platform={preview.plan.platform}
          type={preview.plan.postType}
          plan={preview.plan}
          linkedAssets={preview.linkedAssets}
          brandkit={preview.brandkit}
          showMetadata={false}
          className="w-full"
          sceneStageRef={sceneStageRef}
        />
      ) : preview.previewAsset ? (
        <img
          src={preview.previewAsset.signedUrl}
          alt={
            preview.caption.trim() ||
            intl.formatMessage({ id: "approvals.postPreviewAlt", defaultMessage: "Post preview" })
          }
          className="block w-full object-cover"
          style={{
            aspectRatio: `${preview.previewAsset.width ?? 1080} / ${preview.previewAsset.height ?? 1350}`,
          }}
          loading="lazy"
        />
      ) : (
        <div className="flex aspect-4/5 w-full items-center justify-center text-xs text-muted-foreground">
          <FormattedMessage id="common.pending" defaultMessage="Pending" />
        </div>
      )}

      {showStatusBadge && (
        <div className="absolute left-3 top-3">
          <span
            className={cn(
              "inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold backdrop-blur-sm",
              post.approvalStatus === "approved" && "bg-emerald-100/90 text-emerald-800",
              post.approvalStatus === "rejected" && "bg-red-100/90 text-red-700",
              post.status !== "generated" && "bg-white/80 text-muted-foreground",
            )}
          >
            {statusLabel}
          </span>
        </div>
      )}
    </div>
  );

  const reviewActions = () => (
    <>
      {canEdit ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="h-8 rounded-lg px-3 text-[12px] font-extrabold text-foreground"
          onClick={openFocusedEditor}
        >
          <FormattedMessage id="common.edit" defaultMessage="Edit" />
        </Button>
      ) : null}
      {actions.canConnectInstagram ? (
        <Button
          type="button"
          size="sm"
          variant="brand"
          className="h-8 rounded-lg px-4 text-[12px] font-extrabold"
          onClick={openInstagramIntegrations}
          disabled={approving || rejecting || publishing}
        >
          <FormattedMessage id="approvals.connectInstagram" defaultMessage="Connect Instagram" />
        </Button>
      ) : actions.canDownload ? (
        <Button
          type="button"
          size="sm"
          variant="brand"
          className="h-8 rounded-lg px-4 text-[12px] font-extrabold"
          onClick={() => void downloadImage()}
          disabled={downloading || approving || rejecting || publishing}
        >
          {downloading ? (
            "…"
          ) : (
            <FormattedMessage id="approvals.downloadImage" defaultMessage="Download image" />
          )}
        </Button>
      ) : actions.canApprove ? (
        <Button
          type="button"
          size="sm"
          variant="brand"
          className="h-8 rounded-lg px-4 text-[12px] font-extrabold"
          onClick={onApprove}
          disabled={approving || rejecting || publishing}
        >
          {approving ? "…" : <FormattedMessage id="common.approve" defaultMessage="Approve" />}
        </Button>
      ) : actions.showPublishNow ? (
        <Button
          type="button"
          size="sm"
          variant="brand"
          className="h-8 rounded-lg px-4 text-[12px] font-extrabold"
          onClick={onPublishNow}
          disabled={!actions.canPublishNow || approving || rejecting || publishing}
        >
          {publishing || actions.publishPending ? (
            <FormattedMessage id="approvals.publishNowPending" defaultMessage="Publishing…" />
          ) : (
            <FormattedMessage id="approvals.publishNow" defaultMessage="Publish now" />
          )}
        </Button>
      ) : null}
    </>
  );

  return (
    <article
      className={cn(
        "group flex h-full w-full flex-col overflow-hidden rounded-lg border border-border bg-card shadow-soft transition-all hover:-translate-y-0.5 hover:shadow-md focus-within:border-input",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2.5 px-2.5 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[conic-gradient(from_210deg,#feda75,#fa7e1e,#d62976,#4f5bd5,#feda75)] p-[1.5px]">
            <span className="flex h-full w-full items-center justify-center rounded-full bg-card text-[10px] font-extrabold text-foreground">
              {profileInitials}
            </span>
          </span>
          <div className="min-w-0">
            <p className="truncate text-[11px] font-extrabold leading-4 text-foreground">
              {profileLabel}
            </p>
            <p className="truncate text-[10px] font-medium leading-3 text-muted-foreground">
              {postTypeLabel}
            </p>
          </div>
        </div>
        <PostPreviewOverflowMenu
          size="compact"
          downloading={downloading}
          rejecting={approving || rejecting}
          onDownload={() => void downloadImage()}
          onReject={actions.canReject ? onReject : undefined}
          triggerClassName="h-9 w-9"
        />
      </div>

      <div className="relative overflow-hidden">
        <button
          type="button"
          onClick={onOpen ?? openFocusedEditor}
          className="block w-full cursor-zoom-in text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {visualContent}
        </button>

        {(canEdit || actions.primaryAction) && (
          <div className="pointer-events-none absolute inset-0 z-10 hidden items-center justify-center bg-black/0 opacity-0 transition-[background-color,opacity] duration-200 group-hover:bg-black/16 group-hover:opacity-100 group-focus-within:bg-black/16 group-focus-within:opacity-100 sm:flex">
            <div className="pointer-events-auto flex items-center gap-2">{reviewActions()}</div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2.5 px-2.5 py-1.5 text-foreground">
        <Heart className="h-4 w-4 stroke-[1.8]" aria-hidden={true} />
        <MessageCircle className="h-4 w-4 stroke-[1.8]" aria-hidden={true} />
        <Send className="h-4 w-4 stroke-[1.8]" aria-hidden={true} />
        <Bookmark className="ml-auto h-4 w-4 stroke-[1.8]" aria-hidden={true} />
      </div>

      <div className="mt-auto flex flex-1 flex-col px-2.5 pb-2.5">
        <div className="space-y-1">
          <p className="text-[10px] font-bold text-foreground">
            <FormattedMessage
              id="approvals.scheduledFor"
              defaultMessage="Scheduled {time}"
              values={{ time: scheduledLabel }}
            />
          </p>
          <p className="line-clamp-2 text-[11px] leading-[1.4] text-foreground">
            <span className="font-extrabold">{profileLabel}</span>{" "}
            <span className="font-semibold">{title}</span>{" "}
            <span className="text-muted-foreground">
              {excerpt || preview.caption.trim() || captionFallback}
            </span>
          </p>
          {preview.hashtags.length > 0 ? (
            <p className="line-clamp-1 text-[10px] font-semibold text-info">
              {preview.hashtags.map((tag) => `#${tag.replace(/^#/, "")}`).join(" ")}
            </p>
          ) : null}
        </div>

        {(canEdit || actions.primaryAction) && (
          <div className="mt-2 flex items-center gap-2 border-t border-border pt-2 sm:hidden">
            {reviewActions()}
          </div>
        )}
      </div>
    </article>
  );
}
